// ============================================================================
// WORKOUT DISPLAY UTILITIES
// Formatting functions matching iOS app behavior
// ============================================================================

import type {
  DurationType,
  EquipmentType,
  Exercise,
  GeneralActivityType,
  StructuredWorkoutData,
  TargetType,
  RestDurationType,
  WorkoutRepeatGroup,
  WorkoutStep,
  WorkoutStepElement,
  WorkoutTarget,
} from '@/types/workouts'
import {
  EQUIPMENT_DISPLAY,
  SPORT_DISPLAY,
  DURATION_SPEC,
  activityTypeToSport,
  isRepeatGroup,
} from '@/types/workouts'
import {
  BLOCK_SPECS,
  blockParamToWrite,
  formatBlockParam,
  tokenForBlockType,
} from '@/lib/notation/blockTypes'
import { beoflowColors } from '@/theme/muiTheme'

export function sanitizeWorkoutDataForSave(workout: StructuredWorkoutData): StructuredWorkoutData {
  const sanitized = structuredClone(workout) as StructuredWorkoutData & { id?: number }
  delete sanitized.id
  return sanitized
}

/** Shape a workout for the activity-event API, whose Pydantic schema is stricter
 *  than the template API's: top-level steps must be WorkoutRepeatGroups (with
 *  `repeat_count` and `exercise_id`), inner steps must be WorkoutSteps (and must
 *  NOT carry `repeat_count`, which is the discriminator). Mirrors iOS
 *  WorkoutNormalization.swift.
 *
 *  Lifted out of useEventWorkoutEditor so the builder's event-save path and the
 *  completed-activity editor share one implementation rather than drifting. */
export function sanitizeWorkoutDataForEventSave(workout: StructuredWorkoutData): StructuredWorkoutData {
  const sanitized = structuredClone(workout)

  // Uppercasing alone left display labels like "Strength Training" as
  // "STRENGTH TRAINING", which the backend validator rejects with a 500.
  if (sanitized.sport) {
    sanitized.sport = activityTypeToSport(sanitized.sport as string)
  }

  const sanitizeInnerStep = (step: Record<string, unknown>) => {
    delete step.repeat_count
    // Group-only fields that leak from the frontend types
    delete step.superset_number
    delete step.repeats_visible
    delete step.equipment
    delete step.thumbnail_url

    const targets = step.targets as Record<string, unknown>[] | undefined
    if (Array.isArray(targets)) {
      for (const t of targets) {
        if (t.specifier && t.specifier !== 'range' && t.value == null) {
          delete t.specifier
        }
      }
    }
  }

  const sanitizeGroup = (group: Record<string, unknown>) => {
    if (!group.repeat_count || (group.repeat_count as number) < 1) {
      group.repeat_count = 1
    }
    if (!group.exercise_id) {
      group.exercise_id = 0
    }
    const nested = group.steps as Record<string, unknown>[] | undefined
    if (Array.isArray(nested)) {
      for (const inner of nested) {
        // A nested group is a superset; anything else is a plain set.
        if (Array.isArray(inner.steps)) sanitizeGroup(inner)
        else sanitizeInnerStep(inner)
      }
    }
  }

  if (sanitized.steps) {
    for (const step of sanitized.steps as unknown as Record<string, unknown>[]) {
      sanitizeGroup(step)
    }
  }

  return sanitized
}

// ---------------------------------------------------------------------------
// Duration formatting
// ---------------------------------------------------------------------------

/** Format seconds as MM:SS or Ns (matches iOS SetRow.formatDuration) */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, '0')}`
  }
  return `${secs}s`
}

/** Parse h:mm:ss / mm:ss (or just seconds) string into total seconds */
export function parseTimeInput(input: string): number | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  // h:mm:ss or mm:ss format
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':').map((p) => parseInt(p || '0', 10))
    if (parts.some((n) => isNaN(n))) return null
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
    if (parts.length === 2) return parts[0] * 60 + parts[1]
    return parts[0]
  }
  // Plain number → treat as seconds
  const num = Number(trimmed)
  return isNaN(num) ? null : num
}

/** Format seconds as h:mm:ss (hours shown only when >= 1h), else mm:ss */
export function formatTimeDisplay(seconds: number | null): string {
  if (seconds == null) return ''
  const total = Math.max(0, Math.round(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

/** Format a duration value based on its type.
 *
 *  Took a `unit` it never read: the distance branch assumes metres whatever it
 *  is handed, so the parameter promised a conversion that does not happen.
 *  Dropped rather than left as a signature a caller could trust. (The same
 *  assumption in `formatStepDuration` IS reachable — pinned in
 *  roundtrip.test.ts as the km-read-as-metres bug.) */
export function formatDurationValue(
  type: DurationType | null,
  value: number | null,
): string {
  if (value == null || type == null) return ''
  switch (type) {
    case 'time':
      return formatDuration(value)
    case 'distance':
      if (value >= 1000) {
        const km = value / 1000
        return km === Math.floor(km) ? `${km}km` : `${km.toFixed(1)}km`
      }
      return `${Math.round(value)}m`
    case 'reps':
      return `${Math.round(value)}`
    case 'calories':
      return `${Math.round(value)}cal`
    case 'kilojoules':
      return `${Math.round(value)}kJ`
    case 'laps':
      return `${Math.round(value)} ${Math.round(value) === 1 ? 'lap' : 'laps'}`
    case 'lap_button':
      // Retired; still rendered for stored workouts. Named for the athlete
      // action so it can never be misread as a lap COUNT (that is `laps`).
      return 'Until lap button'
    default:
      return `${value}`
  }
}


// ---------------------------------------------------------------------------
// Rest type definitions
// ---------------------------------------------------------------------------

export const REST_TYPES: { type: RestDurationType; label: string; unit?: string }[] = [
  { type: 'time', label: 'Rest', unit: 'time' },
  { type: 'hr_less_than', label: 'Rest', unit: 'HR < bpm' },
]
// ---------------------------------------------------------------------------
// Tonnage formatting
// ---------------------------------------------------------------------------

/** Format tonnage (kg). Whole numbers omit decimals, others show one decimal */
export function formatTonnage(kg: number, reps?: number): string {
  const total = reps != null ? kg * reps : kg
  if (total === Math.floor(total)) {
    return `${total}kg`
  }
  return `${total.toFixed(1)}kg`
}

// ---------------------------------------------------------------------------
// Target formatting
// ---------------------------------------------------------------------------

const TARGET_DISPLAY: Record<TargetType, { name: string; unit: string }> = {
  heartrate_zone:              { name: 'HR',                unit: 'Zone' },
  power_zone:                  { name: 'Power',             unit: 'Zone' },
  pace_zone:                   { name: 'Pace',              unit: 'Zone' },
  heartrate_percent_max:       { name: 'HR % Max',          unit: '%' },
  heartrate_percent_threshold: { name: 'HR % Threshold',    unit: '%' },
  power_percent_ft:            { name: 'Power % FTP',       unit: '%' },
  pace_percent_ft:             { name: 'Pace % FT',         unit: '%' },
  // "HR", matching heartrate_zone and the HR % entries above — "Heart Rate"
  // was the only unabbreviated one and overran a 64px column.
  heartrate_bpm:               { name: 'HR',                unit: 'bpm' },
  power_watts:                 { name: 'Power',             unit: 'W' },
  speed_mps:                   { name: 'Pace',              unit: 'm/s' },
  // No unit suffix: formatTargetValue renders it as mm:ss, and "1:30 laptime"
  // in a cell reads as a stray word rather than a unit.
  lap_time_seconds:            { name: 'Lap',               unit: 'time' },
  cadence_rpm:                 { name: 'Cadence',           unit: 'rpm' },
  weight_kg:                   { name: 'Weight',            unit: 'kg' },
  bar_speed_mps:               { name: 'Bar Speed',         unit: 'm/s' },
  rpe:                         { name: 'RPE',               unit: '/10' },
  open:                        { name: 'Open',              unit: '' },
}

/** Get the unit string for a target type. Speed is sport-aware (km/h vs /km). */
export function getTargetUnit(type: TargetType, sport?: string): string {
  if (type === 'speed_mps') return sport && KMH_SPORTS.has(sport) ? 'km/h' : '/km'
  return TARGET_DISPLAY[type]?.unit ?? ''
}

/** Get the human-readable display name for a target type */
export function getTargetDisplayName(type: TargetType): string {
  return TARGET_DISPLAY[type]?.name ?? type
}

/** Get display name with unit appended, e.g. "Weight (kg)", "RPE (/10)" */
export function getTargetLabelWithUnit(type: TargetType): string {
  const info = TARGET_DISPLAY[type]
  if (!info) return type
  if (!info.unit) return info.name
  return `${info.name} (${info.unit})`
}

// All four read DURATION_TYPE_SPECS — see types/workouts.ts. Adding a volume
// type must not mean editing these.

/** Get the unit string for a duration type (matches iOS getDurationUnit) */
export function getDurationUnit(type: DurationType | null): string {
  return (type && DURATION_SPEC[type]?.unit) || ''
}

/** Get duration label without unit (unit shown separately) */
export function getDurationLabel(type: DurationType | null): string {
  return (type && DURATION_SPEC[type]?.shortLabel) || 'Volume'
}

/** Get duration label with unit where applicable */
export function getDurationLabelWithUnit(type: DurationType | null): string {
  const spec = type ? DURATION_SPEC[type] : undefined
  if (!spec) return 'Volume'
  return spec.unit ? `${spec.shortLabel} (${spec.unit})` : spec.shortLabel
}

/** Input constraints for duration types */
export function getDurationConstraints(type: DurationType | null): { min?: number; max?: number; step?: number } {
  return (type && DURATION_SPEC[type]?.constraints) || {}
}

/** Input constraints for target types */
export function getTargetConstraints(type: TargetType): { min?: number; max?: number; step?: number } {
  switch (type) {
    case 'weight_kg':                   return { min: 0, step: 2.5 }
    case 'rpe':                         return { min: 1, max: 10, step: 0.5 }
    case 'bar_speed_mps':               return { min: 0, max: 5, step: 0.01 }
    case 'heartrate_zone':
    case 'pace_zone':                   return { min: 1, max: 7, step: 0.5 }
    case 'power_zone':                  return { min: 0, max: 7, step: 0.5 }
    case 'heartrate_bpm':               return { min: 30, max: 250, step: 1 }
    case 'power_watts':                 return { min: 0, max: 3000, step: 5 }
    case 'cadence_rpm':                 return { min: 0, max: 300, step: 1 }
    case 'speed_mps':                   return { min: 0, max: 15, step: 0.1 }
    case 'heartrate_percent_max':
    case 'heartrate_percent_threshold':
    case 'power_percent_ft':
    case 'pace_percent_ft':             return { min: 0, max: 200, step: 1 }
    default:                            return {}
  }
}

// Sports whose speed targets read as km/h; everything else reads as pace (time/km).
const KMH_SPORTS = new Set(['BIKING', 'SKATING'])

/** m/s -> 'm:ss' pace number (no unit). */
function paceNumPerKm(vMps: number): string {
  const totalSec = Math.round(1000 / Math.max(vMps, 0.1))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/** m/s -> 'N' km/h number (1 dp, trailing .0 dropped, no unit). */
function kmhNum(vMps: number): string {
  return (vMps * 3.6).toFixed(1).replace(/\.0$/, '')
}

/** Unit suffix (leading space) for a speed target, sport-aware. */
function speedUnit(sport?: string): string {
  // "per km", not "/km": a word reads aloud, autocompletes, and never meets
  // the slash that opens a rest. Readers accept "/km" and "min/km" too.
  return sport && KMH_SPORTS.has(sport) ? ' km/h' : ' per km'
}

/** Numeric part of a speed target, sport-aware (km/h for bike/skate, else pace). */
function speedNum(vMps: number, sport?: string): string {
  return sport && KMH_SPORTS.has(sport) ? kmhNum(vMps) : paceNumPerKm(vMps)
}

/** Full speed target string, e.g. '4:10 per km' or '30 km/h'. */
function formatSpeed(vMps: number, sport?: string): string {
  return `${speedNum(vMps, sport)}${speedUnit(sport)}`
}

/** Canonical lap-time NOTATION number: 'm:ss', with tenths only when non-zero
 *  ('1:10' for 70 s, '1:10.4' for 70.4 s, '0:45' for 45 s).
 *
 *  Not `formatTargetValue(v, 'lap_time_seconds')`, which is the GRID CELL form:
 *  that one writes a bare '45s' under a minute and rounds tenths away over one,
 *  because the column header already says "Lap / time". Notation has no header
 *  to lean on and has to read back, so it always writes the clock and keeps the
 *  tenth. The trailing '.0' is what the old Django writer emitted and what
 *  neither reader's clock pattern accepted, so it is the one form never
 *  written: emitting tenths unconditionally is what dropped the step. */
function lapTimeNum(seconds: number): string {
  const tenths = Math.max(0, Math.round(seconds * 10))
  const mins = Math.floor(tenths / 600)
  const secs = Math.floor((tenths % 600) / 10)
  const tenth = tenths % 10
  return `${mins}:${String(secs).padStart(2, '0')}${tenth ? `.${tenth}` : ''}`
}

/** Parse what a lap-time CELL shows, and what a coach types into one: 'm:ss',
 *  'm:ss.t', a bare '45.5s', or plain seconds. The inverse of
 *  `formatTargetValue`'s lap branch, and deliberately tolerant of BOTH shapes
 *  that branch emits, because an editable cell re-reads its own rendered text.
 *
 *  `parseTimeInput` cannot be reused: it `parseInt`s each part, so '1:10.4'
 *  comes back 70 and the tenth is gone. Widening that one instead would have
 *  let tenths into every duration and rest in the app, which nothing asked for.
 *  Returns null for anything unparseable so the caller can REJECT the edit --
 *  null from a non-empty cell must never be read as "clear the target". */
export function parseLapTimeInput(input: string): number | null {
  // A lap time runs forwards and is finite. Number() also accepts 'Infinity'
  // and '-1:10', and neither survives the round trip this function exists to
  // hold up: Infinity renders 'Infinity:NaN', which reads back as null, and a
  // negative renders '-50s', which stores a lap nobody can run. Rejected here,
  // at the one boundary where typed text becomes a stored value, rather than
  // guarded again at every renderer.
  const finite = (n: number): number | null =>
    Number.isFinite(n) && n >= 0 ? Math.round(n * 10) / 10 : null

  const trimmed = input.trim().replace(/s$/i, '').trim()
  if (!trimmed) return null
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':')
    // A lap is minutes and seconds. An 'h:mm:ss' lap is a typo, not a 3-hour
    // lap, and silently reading its first two parts would invent a value.
    if (parts.length !== 2) return null
    const mins = Number(parts[0] === '' ? '0' : parts[0])
    const secs = Number(parts[1] === '' ? '0' : parts[1])
    if (Number.isNaN(mins) || Number.isNaN(secs)) return null
    // '1:-10' is not 50 seconds. Either side going negative is a typo, and
    // netting them off would invent a time the coach never typed.
    if (mins < 0 || secs < 0) return null
    return finite(mins * 60 + secs)
  }
  return finite(Number(trimmed))
}

/** Format a single numeric target value based on its type */
function formatTargetValue(value: number, type: TargetType): string {
  switch (type) {
    case 'weight_kg':
    case 'rpe':                  // RPE 7.5 is a real prescription, not an 8
      return value === Math.floor(value) ? `${value}` : value.toFixed(1)
    case 'bar_speed_mps':
      return value.toFixed(2)
    case 'lap_time_seconds':
      // A lap time is a TIME. It reads as mm:ss like every other time in the
      // app, not as "90 laptime" -- the token is how you type it, not how it
      // is read back. ONE form for the whole column: a cell that switched to
      // bare seconds under a minute put "1:10, 1:10.4, 45s" down a single
      // column, which reads as three different quantities and stops the times
      // lining up under each other. The header already says "Lap / time", so
      // the `s` was repeating the unit the column had named.
      //
      // lapTimeNum, NOT formatDuration, which rounds the seconds whole and so
      // rendered a 70.4 s lap as "1:10". That was display-only until the cell
      // became editable; now the cell re-reads its own rendered text, so a
      // truncating display turns one unrelated edit into silent data loss.
      return lapTimeNum(value)
    case 'heartrate_bpm':
    case 'power_watts':
    case 'cadence_rpm':
      return `${Math.round(value)}`
    case 'heartrate_zone':
    case 'power_zone':
    case 'pace_zone':
      // Zones support half steps (e.g. Z5.5) — keep one decimal, drop trailing .0
      return value % 1 === 0 ? `${value}` : value.toFixed(1)
    case 'heartrate_percent_max':
    case 'heartrate_percent_threshold':
    case 'power_percent_ft':
    case 'pace_percent_ft':
      return `${Math.round(value)}`
    default:
      return value % 1 === 0 ? `${value}` : value.toFixed(1)
  }
}

/** Format a WorkoutTarget for display (handles range, equal, above, below) */
export function formatTarget(target: WorkoutTarget, sport?: string): string {
  const { type, specifier, value, value_low, value_high } = target

  // Speed is sport-aware (km/h for bike/skate, else time/km) and renders as the
  // NUMBER ALONE. The column header already carries the unit — getTargetUnit
  // returns the same sport-aware "km/h" / "/km" — so appending it here printed
  // it twice, once in the label and again in every cell under it. Same rule as
  // lap_time_seconds below.
  if (type === 'speed_mps') {
    if (specifier === 'range' && value_low != null && value_high != null) {
      return `${speedNum(value_low, sport)}-${speedNum(value_high, sport)}`
    }
    const v = value ?? value_low ?? value_high
    if (v == null) return ''
    const f = speedNum(v, sport)
    if (specifier === 'above') return `>${f}`
    if (specifier === 'below') return `<${f}`
    return f
  }

  // A lap time renders as a time and nothing else. `unit` here is the chip's
  // sub-label ("Lap" over "time", like "RPE" over "/10"); gluing it to the
  // value would read "1:30time". speed_mps opts out the same way above.
  if (type === 'lap_time_seconds') {
    if (specifier === 'range' && value_low != null && value_high != null) {
      return `${formatTargetValue(value_low, type)}-${formatTargetValue(value_high, type)}`
    }
    const v = value ?? value_low ?? value_high
    if (v == null) return ''
    const f = formatTargetValue(v, type)
    if (specifier === 'above') return `>${f}`
    if (specifier === 'below') return `<${f}`
    return f
  }

  const unit = getTargetUnit(type)

  const isZone = type === 'heartrate_zone' || type === 'power_zone' || type === 'pace_zone'

  if (specifier === 'range' && value_low != null && value_high != null) {
    if (isZone) {
      // "Zone" is a PREFIX, not a suffix: a bare "2-3" doesn't say it's a zone.
      return `${unit} ${formatTargetValue(value_low, type)}-${formatTargetValue(value_high, type)}`
    }
    return `${formatTargetValue(value_low, type)}-${formatTargetValue(value_high, type)}${unit}`
  }

  const v = value ?? value_low ?? value_high
  if (v == null) return type === 'open' ? 'Open' : ''

  const formatted = formatTargetValue(v, type)
  if (isZone) return `${unit} ${formatted}`

  const suffix = unit ? `${unit}` : ''
  switch (specifier) {
    case 'above':
      return `>${formatted}${suffix}`
    case 'below':
      return `<${formatted}${suffix}`
    default:
      return `${formatted}${suffix}`
  }
}

// ---------------------------------------------------------------------------
// Sport helpers (re-export + extend from workouts.ts)
// ---------------------------------------------------------------------------

/** Get display color for a sport (delegates to SPORT_DISPLAY) */
export function getSportColor(sport: GeneralActivityType): string {
  return SPORT_DISPLAY[sport]?.color ?? '#F8F6F4'
}

/** Get display name for a sport (delegates to SPORT_DISPLAY) */
export function getSportDisplayName(sport: GeneralActivityType): string {
  return SPORT_DISPLAY[sport]?.name ?? sport
}

/** Get MUI icon name for a sport */
export function getSportIcon(sport: GeneralActivityType): string {
  const icons: Record<GeneralActivityType, string> = {
    HYBRID:      'FitnessCenter',
    AEROBIC:     'Favorite',
    RUNNING:     'DirectionsRun',
    BIKING:      'DirectionsBike',
    SKATING:     'IceSkating',
    SWIMMING:    'Pool',
    STRENGTH:    'FitnessCenter',
    PADDLING:    'Kayaking',
    OTHER:       'MoreHoriz',
    MULTISPORT:  'SportsScore',
    MINDFULNESS: 'SelfImprovement',
  }
  return icons[sport] ?? 'FitnessCenter'
}

// ---------------------------------------------------------------------------
// Equipment helpers (re-export + extend from workouts.ts)
// ---------------------------------------------------------------------------

/** Get display name for an equipment type */
export function getEquipmentDisplayName(type: EquipmentType): string {
  return EQUIPMENT_DISPLAY[type]?.label ?? type
}

/** Get MUI icon name for an equipment type */
export function getEquipmentIcon(type: EquipmentType): string {
  return EQUIPMENT_DISPLAY[type]?.icon ?? 'fitness_center'
}

// ---------------------------------------------------------------------------
// Default mappings
// ---------------------------------------------------------------------------

/** Get default target types appropriate for a duration type */
export function getDefaultTargetTypes(durationType: DurationType): TargetType[] {
  const spec = DURATION_SPEC[durationType]
  return spec ? ([...spec.defaultTargets] as TargetType[]) : ['open']
}

/** Get default duration type for a given sport */
export function getDefaultDurationType(sport: GeneralActivityType): DurationType {
  switch (sport) {
    case 'STRENGTH':
      return 'reps'
    case 'RUNNING':
    case 'BIKING':
    case 'SWIMMING':
    case 'SKATING':
      return 'distance'
    case 'MINDFULNESS':
      return 'time'
    default:
      return 'time'
  }
}

// ---------------------------------------------------------------------------
// Step / group summary
// ---------------------------------------------------------------------------

/** Format a single workout step as a concise summary string */
export function formatStepSummary(step: WorkoutStep, sport?: string): string {
  const parts: string[] = []

  const duration = formatDurationValue(step.duration_type, step.duration_value)
  if (duration) parts.push(duration)

  const targetStrs = (step.targets ?? [])
    .filter((t) => t.type !== 'open')
    .map((t) => formatTarget(t, sport))
    .filter(Boolean)

  if (targetStrs.length > 0) {
    parts.push(`@ ${targetStrs.join(', ')}`)
  }

  return parts.join(' ')
}

// ---------------------------------------------------------------------------
// ID generation (crypto.randomUUID unavailable in insecure HTTP contexts)
// ---------------------------------------------------------------------------

export function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/**
 * Fill in missing `id`s on workout steps/groups loaded from the API.
 *
 * The backend does not persist the frontend-only `id`, so groups and sets arrive
 * with `id: undefined`. Every id-keyed reducer (`mapGroup`, `updateSet`,
 * `updateSetTarget`) matches on `el.id === targetId`, so with all ids undefined
 * a single edit matched EVERY set of EVERY exercise (BEO-2979).
 *
 * Returns a new array; the input is not mutated.
 */
export function ensureStepIds(steps: WorkoutStepElement[]): WorkoutStepElement[] {
  return steps.map((el) => {
    const nested = (el as WorkoutRepeatGroup).steps
    const next = el.id ? el : { ...el, id: generateId() }
    if (!Array.isArray(nested)) return next
    return { ...next, steps: ensureStepIds(nested) } as WorkoutStepElement
  })
}

// ---------------------------------------------------------------------------
// Default step creation
// ---------------------------------------------------------------------------

/** Create a default WorkoutStep from an Exercise (used by ExercisePicker + GhostStepCard) */
export function createDefaultStep(exercise: Exercise): WorkoutStep {
  const isStrength = exercise.equipment
    ? ['barbell', 'dumbbell', 'kettlebell', 'machine', 'bodyweight', 'banded'].includes(exercise.equipment)
    : false

  return {
    id: generateId(),
    name: exercise.name,
    intensity: 'active',
    duration_type: isStrength ? 'reps' : 'time',
    duration_value: isStrength ? 10 : 60,
    duration_unit: null,
    targets: isStrength
      ? [{ type: 'weight_kg', value: null, value_low: null, value_high: null, specifier: 'equal' }]
      : [],
    exercise_id: exercise.id,
    rest_time: isStrength ? 90 : null,
    rest_type: isStrength ? 'time' : 'open',
    completed: false,
    completion_notes: null,
  }
}

/** Format a repeat group as a concise summary string */
export function formatRepeatGroupSummary(group: WorkoutRepeatGroup): string {
  const stepCount = group.steps?.length ?? 0
  const prefix = group.repeat_count > 1 ? `${group.repeat_count}x` : ''

  if (group.name) {
    return prefix ? `${prefix} ${group.name}` : group.name
  }

  if (stepCount === 0) return prefix || '(empty group)'

  // Summarize child steps
  const stepNames = (group.steps ?? [])
    .map((el: WorkoutStepElement) => {
      if (isRepeatGroup(el)) return el.name ?? 'Group'
      return el.name ?? 'Exercise'
    })

  if (group.superset_number != null) {
    const label = stepNames.join(' + ')
    return prefix ? `${prefix} Superset: ${label}` : `Superset: ${label}`
  }

  const label = stepNames.join(', ')
  return prefix ? `${prefix} ${label}` : label
}

// ---------------------------------------------------------------------------
// Exercise group creation (iOS-aligned builder)
// ---------------------------------------------------------------------------

/** Create a WorkoutRepeatGroup for an exercise with one initial set */
export function createDefaultExerciseGroup(exercise: Exercise): WorkoutRepeatGroup {
  const isStrength = exercise.equipment
    ? ['barbell', 'dumbbell', 'kettlebell', 'machine', 'bodyweight', 'banded'].includes(exercise.equipment)
    : false

  const initialStep: WorkoutStep = {
    id: generateId(),
    name: exercise.name,
    intensity: 'active',
    duration_type: isStrength ? 'reps' : 'time',
    duration_value: isStrength ? 10 : 60,
    duration_unit: null,
    targets: isStrength
      ? [{ type: 'weight_kg', value: null, value_low: null, value_high: null, specifier: 'equal' }]
      : [],
    exercise_id: exercise.id,
    rest_time: isStrength ? 90 : null,
    rest_type: isStrength ? 'time' : 'open',
    completed: false,
    completion_notes: null,
  }

  return {
    id: generateId(),
    name: exercise.name,
    repeat_count: 1,
    steps: [initialStep],
    superset_number: null,
    exercise_id: exercise.id,
    repeats_visible: true,
    equipment: exercise.equipment ?? null,
    thumbnail_url: exercise.thumbnail_url ?? null,
  }
}

// ---------------------------------------------------------------------------
// Sport → exercise-selection capability (mirrors iOS GeneralActivityType)
// ---------------------------------------------------------------------------

/** Sports that support picking individual library exercises. STRENGTH/HYBRID and
 *  the catch-all OTHER/MULTISPORT (which have no single representative exercise)
 *  use the library; the remaining sports build interval/set-group steps instead. */
export function supportsExerciseLibrary(sport: GeneralActivityType | string | null | undefined): boolean {
  return sport === 'STRENGTH' || sport === 'HYBRID' || sport === 'OTHER' || sport === 'MULTISPORT'
}

/** The single library exercise that backs a cardio "set group", keyed by sport.
 *  Every set-group sport maps to exactly one real exercise, so the backend-required
 *  exercise_id is always meaningful — no arbitrary fallback. AEROBIC/MINDFULNESS use
 *  dedicated generic exercises ("Cardio"/"Mindfulness"). STRENGTH/HYBRID/OTHER/
 *  MULTISPORT use the exercise library, so they never appear here. */
const CARDIO_SET_GROUP_EXERCISE_NAME: Partial<Record<GeneralActivityType, string>> = {
  RUNNING: 'Running',
  BIKING: 'Biking',
  SWIMMING: 'Swimming',
  SKATING: 'Skating',
  PADDLING: 'Paddling',
  AEROBIC: 'Cardio',
  MINDFULNESS: 'Mindfulness',
}

/** The exercise a non-library sport supplies for every step of a workout, or
 *  null when the sport picks its exercises from the library. The notation reader
 *  uses this as the "lock": under a locked sport the exercise is never typed, so
 *  a line starts at the duration and a leading word is the drill's own name.
 *  Canonicalizes first — a stored `workout_data.sport` may be a raw activity
 *  label ('Ice Skating'), not the enum. */
export function lockedExerciseName(sport: GeneralActivityType | string | null | undefined): string | null {
  if (!sport) return null
  const canonical = activityTypeToSport(sport)
  if (supportsExerciseLibrary(canonical)) return null
  return CARDIO_SET_GROUP_EXERCISE_NAME[canonical] ?? null
}

/** Resolve the library Exercise that backs a cardio set group for a sport.
 *  Returns null if the library hasn't loaded or the exercise is missing (the
 *  caller must not add a group without a valid exercise_id). */
export function resolveCardioSetGroupExercise(
  sport: GeneralActivityType | string,
  exercises: Exercise[] | undefined,
): Exercise | null {
  if (!exercises || exercises.length === 0) return null
  const name = CARDIO_SET_GROUP_EXERCISE_NAME[sport as GeneralActivityType]
  if (!name) return null
  return exercises.find((e) => e.name === name) ?? null
}

/** Default targets for a cardio set group (mirrors iOS createDefaultTargetsForSport). */
function defaultCardioTargets(sport: GeneralActivityType | string): WorkoutTarget[] {
  const t = (type: TargetType, value: number): WorkoutTarget =>
    ({ type, value, value_low: null, value_high: null, specifier: 'equal' })
  switch (sport) {
    case 'AEROBIC':     return [t('heartrate_bpm', 150)]
    case 'RUNNING':     return [t('speed_mps', 3.0)]
    case 'SKATING':     return [t('speed_mps', 6.0)]
    case 'BIKING':      return [t('power_watts', 200)]
    case 'SWIMMING':    return [t('speed_mps', 1.2)]
    case 'MINDFULNESS': return []  // a calm session has no pace/HR/power target
    default:            return [t('rpe', 6)]
  }
}

/** Create a WorkoutRepeatGroup for a non-library sport "set group". Mirrors iOS
 *  `WorkoutViewModel.addCardioSet`: sport-named group with one interval step
 *  (distance for swimming, time otherwise) and sport-default targets. `exercise`
 *  is the resolved library exercise that supplies the backend-required id. */
export function createCardioSetGroup(
  sport: GeneralActivityType | string,
  exercise: Exercise,
): WorkoutRepeatGroup {
  const isSwimming = sport === 'SWIMMING'
  const name = getSportDisplayName(sport as GeneralActivityType)
  const step: WorkoutStep = {
    id: generateId(),
    name,
    intensity: 'active',
    // iOS DefaultDurationValues: distance 100 m (swimming) / time 1800 s (30 min)
    duration_type: isSwimming ? 'distance' : 'time',
    duration_value: isSwimming ? 100 : 1800,
    duration_unit: null,
    targets: defaultCardioTargets(sport),
    exercise_id: exercise.id,
    rest_time: null,
    rest_type: 'open',
    completed: false,
    completion_notes: null,
  }
  return {
    id: generateId(),
    name,
    repeat_count: 1,
    steps: [step],
    superset_number: null,
    exercise_id: exercise.id,
    repeats_visible: true,
    // Media comes from the resolved exercise, the same as every other way a set
    // group gets made. These were pinned to null to mirror iOS `usesSetGroupUI`,
    // but the notation path copies the exercise's thumbnail up (asExerciseGroups
    // and the bracket-close both do), so a cardio group added in the grid was
    // the only one on the page without a picture.
    equipment: exercise.equipment ?? null,
    thumbnail_url: exercise.thumbnail_url ?? null,
  }
}

/** Re-point every set group (and its inner steps) to a cardio sport's single
 *  representative exercise, resetting media + targets to that sport's defaults.
 *  Used when a workout's sport changes to a set-group (cardio) type, so groups
 *  stop referencing the previous sport's exercise. Durations are preserved. */
export function repointGroupsToCardioSport(
  steps: WorkoutStepElement[],
  sport: GeneralActivityType | string,
  exercise: Exercise,
): WorkoutStepElement[] {
  const name = getSportDisplayName(sport as GeneralActivityType)
  const repointStep = (s: WorkoutStep): WorkoutStep => ({
    ...s,
    name,
    exercise_id: exercise.id,
    targets: defaultCardioTargets(sport),
  })
  return steps.map((el) => {
    if (!isRepeatGroup(el)) return repointStep(el)
    return {
      ...el,
      name,
      exercise_id: exercise.id,
      equipment: null,
      thumbnail_url: null,
      steps: (el.steps ?? []).map((inner) =>
        isRepeatGroup(inner) ? inner : repointStep(inner),
      ),
    }
  })
}

/** Clone the last set in a group as a template for "Add Set" */
export function createDefaultSet(group: WorkoutRepeatGroup): WorkoutStep {
  const steps = (group.steps ?? []).filter(
    (el): el is WorkoutStep => !isRepeatGroup(el),
  )
  const lastStep = steps[steps.length - 1]

  if (!lastStep) {
    return {
      id: generateId(),
      name: group.name,
        intensity: 'active',
      duration_type: 'reps',
      duration_value: 10,
      duration_unit: null,
      targets: [],
      exercise_id: group.exercise_id,
      rest_time: null,
      rest_type: 'open',
      completed: false,
      completion_notes: null,
    }
  }

  return {
    id: generateId(),
    name: lastStep.name,
    intensity: lastStep.intensity,
    duration_type: lastStep.duration_type,
    duration_value: lastStep.duration_value,
    duration_unit: lastStep.duration_unit,
    targets: (lastStep.targets ?? []).map((t) => ({ ...t })),
    exercise_id: lastStep.exercise_id,
    rest_time: lastStep.rest_time,
    rest_type: lastStep.rest_type,
    completed: false,
    completion_notes: null,
  }
}

// ---------------------------------------------------------------------------
// Group column headers (for sets table)
// ---------------------------------------------------------------------------

/** Return column header definitions for the sets table */
export function getGroupColumns(
  group: WorkoutRepeatGroup,
): { type: 'duration' | 'target'; targetType?: TargetType; label: string }[] {
  const steps = (group.steps ?? []).filter(
    (el): el is WorkoutStep => !isRepeatGroup(el),
  )

  // Duration column first
  const durationType = steps[0]?.duration_type ?? null
  const columns: { type: 'duration' | 'target'; targetType?: TargetType; label: string }[] = [
    { type: 'duration', label: getDurationLabel(durationType) },
  ]

  // Collect unique target types across all steps
  const seenTargets = new Set<TargetType>()
  for (const step of steps) {
    for (const target of step.targets ?? []) {
      if (!seenTargets.has(target.type)) {
        seenTargets.add(target.type)
        columns.push({
          type: 'target',
          targetType: target.type,
          label: getTargetDisplayName(target.type),
        })
      }
    }
  }

  return columns
}

// ---------------------------------------------------------------------------
// Structured workout -> human-readable text (port of iOS WorkoutTextConverter)
// ---------------------------------------------------------------------------

/** Convert a full StructuredWorkoutData to multi-line human-readable text */
export function convertWorkoutToText(workout: StructuredWorkoutData): string {
  if (!workout.steps.length) return 'No workout structure defined'
  const lines = convertElements(workout.steps, 0, workout.sport)
  // Trim trailing blank lines
  while (lines.length && lines[lines.length - 1] === '') lines.pop()
  return lines.join('\n')
}

function extractBaseName(name: string): string {
  let result = name
  while (result.length && /\d/.test(result[result.length - 1])) {
    result = result.slice(0, -1)
  }
  return result.trim()
}

/** A stored time in whole seconds. min/minutes means the value is in minutes;
 *  otherwise it is seconds. Converted BEFORE rounding: rounding the minutes
 *  first made 1.5 min 120 s here and 90 s in the backend (_to_seconds). */
function timeSeconds(value: number, unit?: string | null): number {
  return Math.round(unit === 'min' || unit === 'minutes' ? value * 60 : value)
}

function formatTimeDuration(value: number, unit?: string | null): string {
  const total = timeSeconds(value, unit)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const parts: string[] = []
  if (h) parts.push(`${h} h`)
  if (m) parts.push(`${m} min`)
  if (s || !parts.length) parts.push(`${s} sec`)
  return parts.join(' ')
}

function getDurationUnitSuffix(step: WorkoutStep): string {
  if (!step.duration_type) return ''
  switch (step.duration_type) {
    case 'time':
      return ''
    case 'distance':
      // The stored unit word is passed through VERBATIM here while the scalar
      // path (formatStepDuration) writes the notation token, so the same step
      // serialized as a list read "(100,200,300) meters" and as a scalar
      // "200 m" — and the reader knows only the token, so the Acceleration
      // Pyramid's list shipped unreadable and the save stayed shut. The two
      // paths must spell the unit the same way: writers are canonical.
      return step.duration_unit === 'meters' || step.duration_unit === 'meter' ? ' m'
        : step.duration_unit === 'kilometers' ? ' km'
        : step.duration_unit ? ` ${step.duration_unit}` : ' m'
    case 'reps':       return ' reps'
    case 'kilojoules': return ' kJ'
    case 'calories':   return ' cal'
    // A lap list without its unit ("Row (4,2)") was text this writer's own
    // reader rejects, which held the save shut; the scalar path writes "4 laps".
    case 'laps':       return ' laps'
    default:           return ''
  }
}

function formatRestTime(seconds: number): string {
  const total = Math.round(seconds)
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

function formatStepDuration(step: WorkoutStep): string {
  if (!step.duration_type || step.duration_value == null) return ''
  const v = step.duration_value
  switch (step.duration_type) {
    case 'time': return formatTimeDuration(v, step.duration_unit)
    case 'distance': {
      const unit = step.duration_unit
      if (unit === 'km' || unit === 'kilometers') {
        const km = v / 1000
        return km === Math.floor(km) ? `${km} km` : `${km.toFixed(1)} km`
      }
      if (v >= 1000) {
        const km = v / 1000
        return km === Math.floor(km) ? `${km} km` : `${km.toFixed(1)} km`
      }
      return `${Math.round(v)} m`
    }
    case 'reps': {
      const n = Math.round(v)
      return `${n} rep${n === 1 ? '' : 's'}`
    }
    case 'laps': {
      const n = Math.round(v)
      return `${n} lap${n === 1 ? '' : 's'}`
    }
    case 'kilojoules': return `${Math.round(v)} kJ`
    case 'calories':   return `${Math.round(v)} cal`
    // Per-type formatting stays bespoke (km rollover, mm:ss, pluralisation),
    // so this switch is not derived from DURATION_TYPE_SPECS. The round-trip
    // test in notation/roundtrip.test.ts walks the table and fails if a type
    // is offered here but written in a form the parser cannot read back.
    default:           return `${v}`
  }
}

function formatSingleTargetText(target: WorkoutTarget, sport?: string): string {
  // A band ("140-150 bpm", "1-2 Zone HR") has to render as a band. Taking the
  // low bound alone silently narrows the prescription, and in text mode that
  // reads back as a single value and destroys the range on save. Mirrors
  // _fmt_target in structured_workout_utils.py: widen the number in place, so
  // the unit is still written once.
  const lo = target.value_low
  const hi = target.value_high
  if (target.value == null && lo != null && hi != null && lo !== hi) {
    const high = formatSingleTargetText({ ...target, value: hi }, sport)
    const loText = formatSingleTargetText({ ...target, value: lo }, sport)
    if (!high) return loText
    const hiNum = high.match(/^[\d.:]+/)?.[0]
    const loNum = loText.match(/^[\d.:]+/)?.[0]
    if (!hiNum || !loNum) return high
    return `${loNum}-${high}`
  }
  const v = target.value ?? lo ?? hi
  if (v == null) return ''
  const iv = Math.round(v)
  // Zones keep a tenth, the same rule formatTargetValue already uses for the
  // card ("Zones support half steps"). Rounding here threw away a decimal zone
  // the rest of the stack supports end to end: the backend interpolates one and
  // applies a 3 percent band, and this is the string the ride is built from, so
  // `2.9 Zone Power` used to reach the road as zone 3.
  const isZone = target.type === 'heartrate_zone' || target.type === 'power_zone' || target.type === 'pace_zone'
  const zv = v % 1 === 0 ? String(iv) : (Math.round(v * 10) / 10).toFixed(1)
  // Every other number keeps its value: 62.5 kg, RPE 7.5. Rounding (here) and
  // truncating (the backend) wrote two different wrong numbers for one target.
  // Whole values print bare; others to at most two places, half up. Mirrors
  // _fmt_num in structured_workout_utils.py.
  const n = Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100)
  switch (target.type) {
    // Number first, like every other target. One shape means the coach types
    // the value and picks the type, instead of remembering which types put the
    // word in front — and it is what makes a varying zone consolidate at all:
    // "Zone 5 Power" and "Zone 1 Power" shared a first token, so a run of them
    // collapsed to one value. Readers accept the zone-first form forever.
    case 'heartrate_zone':          return `${isZone ? zv : iv} Zone HR`
    case 'power_zone':              return `${isZone ? zv : iv} Zone Power`
    case 'pace_zone':               return `${isZone ? zv : iv} Zone Pace`
    case 'heartrate_percent_max':   return `${n}% Max HR`
    case 'heartrate_percent_threshold': return `${n}% LTHR`
    case 'power_percent_ft':        return `${n}% FTP`
    case 'pace_percent_ft':         return `${n}% FTP Pace`
    case 'heartrate_bpm':           return `${n} bpm`
    case 'power_watts':             return `${n} W`
    case 'speed_mps':               return formatSpeed(v, sport)
    case 'cadence_rpm':             return `${n} rpm`
    case 'weight_kg':               return `${n} kg`
    case 'bar_speed_mps':           return `${v.toFixed(2)} m/s bar speed`
    case 'rpe':                     return `${n} RPE`
    // "hold 1:30 per lap". Written with the `laptime` suffix the editor's
    // vocabulary and both parsers already carry; the backend's legacy `lap`
    // suffix stays READABLE forever but is never what we write. Falling through
    // to `default` here is what dropped a lap-time target on the way out of the
    // webapp: formatTargetsText filters the empty string away, so the step
    // serialized with no target at all.
    case 'lap_time_seconds':        return `${lapTimeNum(v)} laptime`
    default:                        return ''
  }
}

/** Every target the step carries, joined by the same `@` that opens the first.
 *
 *  A step holds a LIST of targets and the grid draws a column for each, so a
 *  coach can prescribe "Zone 4-5 pace AND RPE 8" — one effort described two
 *  ways, not a choice between them. This used to emit `targets[0]` and drop the
 *  rest, which made the text lossy against its own data model: switch a
 *  two-target workout into text mode and the second one was gone. The round
 *  trip is the product, so it writes all of them. */
function formatTargetsText(targets: WorkoutTarget[], sport?: string): string {
  return targets
    .map((t) => formatSingleTargetText(t, sport))
    .filter(Boolean)
    .join(' @ ')
}

/** One value list for a run of sets: "(60,70,80) kg".
 *
 *  Splits each formatted target into its leading number and the rest, checks
 *  that the rest is identical across the run, and lists the numbers. Mirrors
 *  `_consolidate_targets` in structured_workout_utils.py so the two writers
 *  emit the same string for the same workout — the corpus is only a contract if
 *  they do.
 *
 *  Returns null when the run cannot be written as one line without losing
 *  something, and the caller then gives every step its own line. That happens
 *  when the target TYPE changes mid-run ("50% Max HR" then "120 kg"), which the
 *  old split-at-the-first-space version spliced into "(50%,120)kg" — a string
 *  no reader can take back apart. */
function consolidateTargetValues(targets: string[]): string | null {
  if (!targets.length) return ''
  if (targets.every((t) => t === targets[0])) return targets[0]

  const values: string[] = []
  let suffix: string | null = null
  for (const t of targets) {
    const m = t.match(/^([\d.:]+)(.*)$/)
    if (!m) return null                       // no leading value to list
    if (suffix === null) suffix = m[2]
    else if (m[2] !== suffix) return null     // a different measurement entirely
    values.push(m[1])
  }
  return `(${values.join(',')})${suffix ?? ''}`
}

function convertElements(elements: WorkoutStepElement[], indentLevel: number, sport?: string): string[] {
  const result: string[] = []
  let i = 0
  while (i < elements.length) {
    const el = elements[i]
    // A superset is N sibling groups sharing a number, one per exercise, and
    // the grammar has one bracket for the set of them. Written per group it
    // came out as N separate SuperSet blocks, which read back as N supersets.
    if (isRepeatGroup(el) && el.superset_number != null) {
      const run = superset(elements, i)
      result.push(...convertSuperset(run, indentLevel, sport))
      i += run.length
      continue
    }
    if (!isRepeatGroup(el) && i + 1 < elements.length) {
      const consolidated = tryConsolidateSteps(elements, i, indentLevel, sport)
      if (consolidated) {
        result.push(...consolidated.lines)
        i = consolidated.nextIndex
        continue
      }
    }
    result.push(...convertElement(el, indentLevel, sport))
    i++
  }
  while (result.length && result[result.length - 1] === '') result.pop()
  return indentLevel === 0 ? endBlocks(result) : result
}

/** A blank line after every top-level block that something follows: before
 *  each unindented line that comes straight after an indented one. The reader
 *  would end the block at the dedent anyway; the blank line is what a person
 *  reads as the end. */
function endBlocks(lines: string[]): string[] {
  const out: string[] = []
  lines.forEach((l, i) => {
    if (i > 0 && l && !l.startsWith(' ') && lines[i - 1].startsWith(' ')) out.push('')
    out.push(l)
  })
  return out
}

/** The consecutive groups that make up one superset. */
function superset(elements: WorkoutStepElement[], start: number): WorkoutRepeatGroup[] {
  const first = elements[start] as WorkoutRepeatGroup
  const run: WorkoutRepeatGroup[] = [first]
  for (let i = start + 1; i < elements.length; i++) {
    const el = elements[i]
    if (!isRepeatGroup(el) || el.superset_number !== first.superset_number) break
    run.push(el as WorkoutRepeatGroup)
  }
  return run
}

/** The bracket header's qualifier: the block's kind and, when it has one, its
 *  clock. `SuperSet` for a null kind keeps every workout written before
 *  block_type existed writing exactly the byte it wrote before.
 *
 *  AMRAP writes NO round count: its rounds are a result, not a prescription,
 *  so `1x AMRAP` would state a prescription nobody made. */
function blockHeader(group: WorkoutRepeatGroup, indent: string, label: string): string {
  // A type this build does not know (stored JSON from a newer client) writes as
  // the plain SuperSet every group wrote before block_type existed.
  const type = group.block_type && Object.hasOwn(BLOCK_SPECS, group.block_type) ? group.block_type : 'superset'
  const token = tokenForBlockType(type) as string
  const param = blockParamToWrite(type, group.block_param_seconds)
  const qualifier = param == null ? token : `${token} ${formatBlockParam(param)}`
  const counted = BLOCK_SPECS[type].counted && group.repeat_count > 1
  return counted
    ? `${indent}${label}${group.repeat_count}x ${qualifier}`
    : `${indent}${label}${qualifier}`
}

/** A group's drill label ("Relays 6x ["): grammar only under a locked sport,
 *  where a leading word cannot be an exercise, and never the sport's own name,
 *  which the reader drops. */
function drillLabel(group: WorkoutRepeatGroup, sport?: string): string {
  const locked = lockedExerciseName(sport)
  return locked && group.name && group.name.toLowerCase() !== locked.toLowerCase() ? `${group.name} ` : ''
}

function convertSuperset(run: WorkoutRepeatGroup[], indentLevel: number, sport?: string): string[] {
  const indent = '  '.repeat(indentLevel)
  const first = run[0]
  // NO label. A superset's siblings are one-per-exercise, so `first.name` is an
  // exercise name, not a name for the pair — writing it produced
  // "Relays 6x SuperSet [ Relays ... ]", which reads as a superset called
  // Relays and says the exercise twice. Only a plain repeat group can carry a
  // drill label (see convertRepeatGroup).
  // A superset of ONE exercise is a drill block ("100m strides 2x SuperSet [")
  // and keeps its label under the same rule as a plain group; the backend
  // writes it too, and the reader keeps it as the card's name.
  const prefix = blockHeader(first, indent, run.length === 1 ? drillLabel(first, sport) : '')
  // Each group's note is written INSIDE the bracket, under the exercise it
  // belongs to. It used to be hoisted: the last group carrying a note had it
  // emitted after the closing bracket, so a note on the second exercise looked
  // like a note on the whole superset. It still round-tripped — a note after
  // `]` re-attaches to the last sibling — but only because the note happened to
  // be on the last one. On the FIRST of two it moved to the second silently.
  // A note on a line inside the bracket attaches to that step, and
  // splitByExercise lifts it back onto that sibling's card.
  return bracket(prefix, run.flatMap((g) => innerWithNote(g, indentLevel, sport)))
}

/** A bracketed group's lines, its note written INSIDE the bracket under the
 *  exercise it describes. Never after the `]` (Daniel, 2026-10-15): out there it
 *  read as a note on whatever came last -- the last superset exercise, or a
 *  sibling -- and a bare `]` line is the bracket's alone. Same rule as a
 *  flattened group: the note may take its sole step's line, and a group of more
 *  than one exercise has no line to put it on (isOneExercise). */
function innerWithNote(group: WorkoutRepeatGroup, indentLevel: number, sport?: string): string[] {
  const steps = group.steps ?? []
  const lines = convertElements(steps, indentLevel + 1, sport)
  if (!isOneExercise(steps)) return lines
  return withNote(lines, group, '  '.repeat(indentLevel + 1), { annotatesTheLineBelow: isOneLine(lines) })
}

/** Whether a group's content wrote exactly one exercise line (a sole step, or
 *  sets that folded together). Then the group's note -- the card's note, the
 *  one the grid shows -- and the line's own note describe the same thing, and
 *  the card's wins. Mirrors _is_one_line in structured_workout_utils.py. */
function isOneLine(lines: string[]): boolean {
  return lines.filter((l) => !RE_WRITTEN_NOTE.test(l.trim())).length === 1
}

function tryConsolidateSteps(
  elements: WorkoutStepElement[],
  startIndex: number,
  indentLevel: number,
  sport?: string,
): { lines: string[]; nextIndex: number } | null {
  const first = elements[startIndex]
  if (isRepeatGroup(first)) return null
  const firstStep = first as WorkoutStep
  const indent = '  '.repeat(indentLevel)
  const firstName = firstStep.name ?? 'Exercise'
  const baseFirst = extractBaseName(firstName)

  const consecutive: WorkoutStep[] = [firstStep]
  let endIndex = startIndex + 1
  while (endIndex < elements.length) {
    const el = elements[endIndex]
    if (isRepeatGroup(el)) break
    const s = el as WorkoutStep
    if (extractBaseName(s.name ?? 'Exercise') !== baseFirst) break
    if (s.duration_type !== firstStep.duration_type) break
    // A run that folds onto one line keeps ONE note, so steps with different
    // notes stay separate lines -- folding them dropped every note but the
    // last. A change of target kinds ends the run too. Both mirror the
    // backend's _try_consolidate_steps.
    if ((s.description ?? '') !== (firstStep.description ?? '')) break
    if (targetKinds(s) !== targetKinds(firstStep)) break
    consecutive.push(s)
    endIndex++
  }
  if (consecutive.length < 2) return null

  let anythingVaries = false
  for (let j = 1; j < consecutive.length; j++) {
    if (consecutive[j].duration_value !== consecutive[j - 1].duration_value ||
        consecutive[j].rest_time !== consecutive[j - 1].rest_time ||
        JSON.stringify(consecutive[j].targets) !== JSON.stringify(consecutive[j - 1].targets)) {
      anythingVaries = true
      break
    }
  }

  // Identical consecutive steps collapse to "name Nx(dur @ tgt / rest)".
  if (!anythingVaries) {
    const showName = baseFirst.toLowerCase() !== 'set'
    const dur = formatStepDuration(firstStep)
    const tgt = formatTargetsText(firstStep.targets ?? [], sport)
    let line = `${indent}${showName ? firstName : 'Exercise'} ${consecutive.length}x(`
    if (dur) line += tgt ? `${dur} @ ${tgt}` : dur
    else if (tgt) line += `@ ${tgt}`
    // The rest goes INSIDE the parentheses: it is each set's, not one rest
    // after all of them (Daniel, 2026-10-15). Mirrors the backend.
    line += writeRest(firstStep)
    line += ')'
    if (showName) line = dropName(line, indent, firstName, sport)
    return { lines: noteFor([line], consecutive, indent), nextIndex: endIndex }
  }

  // A value list splits one formatted target into its number and its suffix,
  // which a step carrying two targets has no single answer for. Rather than
  // consolidate the wrong thing, the run does not become one line and every
  // step keeps its own — less compact, and it loses nothing.
  if (consecutive.some((s) => (s.targets?.length ?? 0) > 1)) return null

  const allTargets: WorkoutTarget[] = []
  const allRestTimes: number[] = []
  for (const s of consecutive) {
    allTargets.push(...(s.targets ?? []))
    // A conditional rest is not a duration, so it cannot join a rest list: it
    // drops out here and the run writes per step instead (writeRest).
    if (s.rest_time != null && (s.rest_type == null || s.rest_type === 'time')) {
      allRestTimes.push(s.rest_time)
    }
  }

  const shouldShowName = baseFirst.toLowerCase() !== 'set'
  let line = shouldShowName ? `${indent}${firstName}` : indent

  const targetStrs = allTargets.map((t) => formatSingleTargetText(t, sport)).filter(Boolean)
  const consolidatedTargets = targetStrs.length ? consolidateTargetValues(targetStrs) : ''
  const consolidatedRests = allRestTimes.length
    ? consolidateTargetValues(allRestTimes.map(formatRestTime)) : ''
  // Nothing this run holds can be written as one line without losing a value,
  // so it does not become one: every step keeps its own.
  if (consolidatedTargets === null || consolidatedRests === null) return null
  // A rest list carries no gaps, so a run where only some steps rest would come
  // back with that rest on all of them.
  if (allRestTimes.length && allRestTimes.length !== consecutive.length) return null

  if (consecutive.every((s) => s.duration_value === firstStep.duration_value)) {
    // The duration is shared by every set in the run, so it is written once —
    // but it IS written. Omitting it turned "5 reps at 60, 70, 80 kg" into
    // "@ (60,70,80)kg", losing the reps: the most common strength shape there
    // is, and the one thing the coach cannot infer back.
    const dur = formatStepDuration(firstStep)
    if (dur) line += shouldShowName ? ` ${dur}` : dur
    if (consolidatedTargets) {
      line += shouldShowName ? ` @ ${consolidatedTargets}` : `(${consecutive.length}x) @ ${consolidatedTargets}`
    }
  } else {
    const durationValues = consecutive
      .map((s) => s.duration_value != null
        ? (s.duration_type === 'time' ? formatTimeDuration(s.duration_value, s.duration_unit) : String(Math.round(s.duration_value)))
        : null)
      .filter(Boolean) as string[]
    if (durationValues.length) {
      let values = durationValues
      let unitSuffix = getDurationUnitSuffix(firstStep)
      if (firstStep.duration_type === 'time') {
        // One list, one unit, the largest every value fits: "(10,30) min",
        // "(30,90) sec", "(1,2) h". Spelling a unit on every value,
        // "(10 min,30 min)", only earned its length on a mixed list, and the
        // seconds fallback covers that. Mirrors _consolidate_durations.
        const secs = consecutive.map((s) => timeSeconds(s.duration_value ?? 0, s.duration_unit))
        const [div, word] = secs.every((t) => t % 3600 === 0) ? [3600, 'h']
          : secs.every((t) => t % 60 === 0) ? [60, 'min'] : [1, 'sec']
        values = secs.map((t) => String(t / div))
        unitSuffix = ` ${word}`
      } else if (firstStep.duration_type === 'distance') {
        // Distances are stored in metres. A list rolls to km when every value
        // does, as a single "5 km" already reads; otherwise it is metres --
        // never a km token on metre values, which read 1000x too long.
        const metres = consecutive.map((s) => s.duration_value ?? 0)
        if (metres.every((m) => m >= 1000)) {
          values = metres.map((m) => String(m / 1000))
          unitSuffix = ' km'
        } else unitSuffix = ' m'
      }
      line += shouldShowName ? ` (${values.join(',')})${unitSuffix}` : `(${values.join(',')})${unitSuffix}`
    }
    if (consolidatedTargets) line += ` @ ${consolidatedTargets}`
  }

  if (consolidatedRests) line += ` / ${consolidatedRests} rest`
  if (shouldShowName) line = dropName(line, indent, firstName, sport)

  return { lines: noteFor([line], consecutive, indent), nextIndex: endIndex }
}

/** A run collapsed onto one line keeps the note of whichever step carried it —
 *  a run only exists where no exercise wrapper does, inside a block. */
function targetKinds(step: WorkoutStep): string {
  return (step.targets ?? []).map((t) => t.type).join(',')
}

function noteFor(lines: string[], run: WorkoutStep[], indent: string): string[] {
  const noted = [...run].reverse().find((s) => s.description)
  return noted ? withNote(lines, noted, indent) : lines
}

function convertElement(element: WorkoutStepElement, indentLevel: number, sport?: string): string[] {
  const indent = '  '.repeat(indentLevel)
  if (isRepeatGroup(element)) {
    return convertRepeatGroup(element, indent, indentLevel, sport)
  }
  return convertStep(element as WorkoutStep, indent, sport)
}

/** Under a single-exercise sport the exercise IS the sport, so a line named
 *  after the sport writes no name: it starts at its duration, and the reader
 *  puts the sport back. Any other name is the coach's set name and stays
 *  (Daniel, 2026-10-15). A line that is nothing but its name keeps it.
 *  Mirrors _drop_name in the backend. */
function dropName(line: string, indent: string, name: string | null | undefined, sport?: string): string {
  const locked = lockedExerciseName(sport)
  if (!locked || !name || name.toLowerCase() !== locked.toLowerCase()) return line
  const head = `${indent}${name} `
  return line.startsWith(head) ? indent + line.slice(head.length) : line
}

function convertStep(step: WorkoutStep, indent: string, sport?: string): string[] {
  let line = `${indent}${step.name ?? 'Exercise'}`
  const dur = formatStepDuration(step)
  const tgt = formatTargetsText(step.targets ?? [], sport)
  if (dur) line += tgt ? ` ${dur} @ ${tgt}` : ` ${dur}`
  else if (tgt) line += ` @ ${tgt}`
  line += writeRest(step)
  return withNote([dropName(line, indent, step.name, sport)], step, indent)
}

/** A rest as the notation writes it, or '' when it cannot be written.
 *
 *  `rest_time` is OVERLOADED: seconds under `rest_type: time`, but a heart rate in
 *  BPM under `hr_less_than`. Formatting it as a time regardless wrote "rest until
 *  HR drops below 120" to the road as `/ 2:00 rest` — a timer where the coach wrote
 *  a condition. `shape.ts` has always refused to read it as a duration; the text
 *  writer did not, and the text is what the ride is built from. */
function writeRest(step: WorkoutStep): string {
  // `!rest_time`, not `== null`: a ZERO rest has always written nothing here and
  // still does in the backend writer (`if rest:`), so testing for null alone made
  // this side emit `/ 0:00 rest` where the other two wrote nothing.
  if (!step.rest_time) return ''
  if (step.rest_type != null && step.rest_type !== 'time') return ''
  return ` / ${formatRestTime(step.rest_time)} rest`
}

/** A note follows what it annotates, on its own line at the same indent —
 *  which is exactly where the reader looks for it. Without this the text was
 *  write-only for notes: they mapped in from the notation, rendered on the
 *  card, and were dropped the moment the workout was serialized back, so
 *  leaving text mode and returning destroyed them. */
function withNote(
  lines: string[], el: { description?: string | null }, indent: string,
  opts?: { annotatesTheLineBelow?: boolean },
): string[] {
  const note = el.description?.replace(/\s+/g, ' ').trim()
  // Quoted, matching what the editor itself writes: normalizeNote canonicalises
  // a note line to `Note: "..."` the moment the coach leaves it. Emitting it
  // bare here meant a note gained quotes as you typed it and lost them again on
  // reload. Both forms parse — RE_NOTE takes anything after the prefix — so
  // this is about the text not changing shape, not about correctness.
  // A double quote inside the note becomes a single one rather than being
  // escaped: the grammar has no escape, and a stray `"` would close the string
  // early. A SINGLE quote is left alone -- stripping it turned "don't" into
  // "dont", which is the webapp writer silently editing a coach's prose.
  if (!note) return lines
  const written = `${indent}Note: "${note.replace(/"/g, "'")}"`
  if (!noteCollides(lines, indent)) return [...lines, written]
  // The grammar gives a line ONE note: a Note attaches to the line above it, so
  // two stacked at the same indent both claim that line and the reader keeps
  // only the last. Something has to give, and WHICH note gives is the whole
  // question -- there are two collisions here and they are not the same.
  //
  // The one we may overwrite: a group that repeats once flattens to a single
  // line while the wrapper AND its step each carry a description. Both notes
  // annotate that one line, so keeping the wrapper's is exactly what the reader
  // would do anyway. Not hypothetical -- every one of the 660 sets in
  // calendar_planning/fixtures/system_workouts.json has a description, so a
  // system workout printed two notes per exercise and silently dropped the
  // first the moment it was read back.
  if (opts?.annotatesTheLineBelow) return [...lines.slice(0, -1), written]
  // The one we may NOT: the line below belongs to a different element -- a
  // sibling step, or a nested group -- and this note has no anchor of its own.
  // Overwriting destroyed that element's note outright AND misattributed this
  // one, printing the wrapper's words against an exercise that never had them,
  // which is worse than the note being absent. Nothing in the grammar can hold
  // a note on a multi-exercise group: a note after `]` re-attaches to the last
  // SIBLING, so even bracketing does not round-trip it. So drop the homeless
  // note and leave the anchored one alone. The gap is the grammar's, and it is
  // the reader that would have to grow the anchor.
  return lines
}

/** Whether a note appended at `indent` would land on a line that already
 *  carries one — the collision the grammar cannot express, since a Note
 *  attaches to the line above and a line holds only one. */
function noteCollides(lines: string[], indent: string): boolean {
  const prev = lines[lines.length - 1]
  return prev !== undefined && prev.startsWith(indent) && RE_WRITTEN_NOTE.test(prev.slice(indent.length))
}

/** A complete note line as THIS writer emits it, with no bracket trailing it.
 *  A nested group's last line is its own `]`, never a note competing for this
 *  indent; the tail anchor also keeps a hugging `Note: "..." ]` out. */
const RE_WRITTEN_NOTE = /^Note: ".*"$/

/** Render a group as its header line with the inner lines one level in, and
 *  no brackets: the indent nests it, and convertElements ends a top-level
 *  block with a blank line, which is how a coach who just presses Enter writes
 *  it (Daniel, 2026-10-15). Bracketed text, one construct per line or hugging,
 *  is still READ everywhere because stored text carries it. Mirrors the
 *  backend _bracket. */
function bracket(prefix: string, inner: string[]): string[] {
  return [prefix, ...inner]
}

function convertRepeatGroup(group: WorkoutRepeatGroup, indent: string, indentLevel: number, sport?: string): string[] {
  const steps = group.steps ?? []
  // A named block ("Relays 6x [") is only grammar under a locked sport, where a
  // leading word cannot be an exercise. Elsewhere the name would read as one and
  // the line would not parse, so it stays unwritten.
  // The sport's own name is not a drill label: the reader drops a leading word
  // equal to the locked exercise, so writing it would not round-trip.
  const label = drillLabel(group, sport)
  if (group.superset_number != null || group.block_type != null) {
    const prefix = blockHeader(group, indent, label)
    return bracket(prefix, innerWithNote(group, indentLevel, sport))
  }
  if (group.repeat_count > 1) {
    return bracket(`${indent}${label}${group.repeat_count}x`, innerWithNote(group, indentLevel, sport))
  }
  // A group that repeats once writes no bracket, so its note follows the lines
  // it flattened to — which is the exercise-line shape the reader puts straight
  // back on the wrapper.
  //
  // That holds only while the wrapper has ONE plain step. A flattened group has
  // no line of its own, so its note lands on whatever line came last, and with
  // more than one child that line belongs to somebody else: a sibling step, or
  // a nested group's closing bracket. Written, the note reads back as THAT
  // element's; collapsed, it destroys that element's own note outright. Both
  // lose a note the coach wrote.
  //
  // Only a wrapper whose sole child is a plain STEP annotates the line below
  // it: the step wrote that line and the wrapper describes the same thing. With
  // more children, or a nested GROUP as the child, the line below is somebody
  // else's and this note must not be written over it — see withNote.
  const lines = convertElements(steps, indentLevel, sport)
  if (!isOneExercise(steps)) return lines
  return withNote(lines, group, indent, { annotatesTheLineBelow: isOneLine(lines) })
}

/** Whether a flattened group's note has a line to sit on. Appended after more
 *  than one exercise (or a nested group's bracket), it reads back as the LAST
 *  one's -- with or without a note of its own there -- so the caller drops it.
 *  Sets of ONE exercise are fine: the note lifts to that exercise's card. */
function isOneExercise(steps: WorkoutStepElement[]): boolean {
  if (steps.some(isRepeatGroup)) return false
  return new Set((steps as WorkoutStep[]).map((s) => s.exercise_id ?? s.name)).size <= 1
}

// ---------------------------------------------------------------------------
// Equipment color mapping
// ---------------------------------------------------------------------------

/** Short labels for equipment badge (fixed-width, uppercase) */
const EQUIPMENT_SHORT_LABEL: Record<string, string> = {
  barbell: 'BB',
  dumbbell: 'DB',
  kettlebell: 'KB',
  machine: 'MCH',
  banded: 'BND',
  bodyweight: 'BW',
  medball: 'MB',
  cardio: 'CDO',
  plyometric: 'PLY',
  mobility: 'MOB',
  stretching: 'STR',
  yoga: 'YGA',
}

/** Get short label for equipment badge display */
export function getEquipmentShortLabel(equipment: string | null): string | null {
  if (!equipment) return null
  return EQUIPMENT_SHORT_LABEL[equipment] ?? null
}

const EQUIPMENT_COLOR_MAP: Record<string, string> = {
  barbell: beoflowColors.exerciseStrengthColor,
  dumbbell: beoflowColors.exerciseStrengthColor,
  kettlebell: beoflowColors.exerciseStrengthColor,
  machine: beoflowColors.exerciseStrengthColor,
  banded: beoflowColors.exerciseFunctionalColor,
  medball: beoflowColors.exerciseFunctionalColor,
  cardio: beoflowColors.exerciseCardioColor,
  mobility: beoflowColors.exerciseRecoveryColor,
  stretching: beoflowColors.exerciseRecoveryColor,
  yoga: beoflowColors.exerciseRecoveryColor,
  bodyweight: beoflowColors.exerciseBodyweightColor,
  plyometric: beoflowColors.exerciseBodyweightColor,
}

/** Get theme color for an equipment type (card header backgrounds) */
export function getEquipmentColor(equipment: string | null): string {
  if (!equipment) return beoflowColors.graphGreyLight
  return EQUIPMENT_COLOR_MAP[equipment] ?? beoflowColors.graphGreyLight
}

// Hydrate exercise-library display fields (thumbnail, equipment) onto each group
// from the loaded exercise list — used by the template load, planned-event load
// and IMPORT paths so the doer/builder shows exercise thumbnails.
//
// A stored workout carries `exercise_id` but no display fields, so any path that
// brings steps in from the server has to run this or every card falls back to the
// equipment icon. The import path did not, which is why an imported workout showed
// no thumbnails at all — and showed no failed image requests either, because the
// `src` was never produced in the first place.
export function hydrateThumbnails(
  steps: WorkoutStepElement[],
  exerciseMap: Map<string | number, Exercise> | null,
): WorkoutStepElement[] {
  if (!exerciseMap) return steps
  return steps.map((el) => {
    if (!isRepeatGroup(el)) return el
    const group = el as WorkoutRepeatGroup
    const ex = exerciseMap.get(group.exercise_id)
    if (!ex) return group
    return {
      ...group,
      thumbnail_url: ex.thumbnail_url ?? group.thumbnail_url,
      equipment: ex.equipment ?? group.equipment,
    }
  })
}

// BEO-3201: custom exercises in a PUBLIC share read carry no display fields —
// sharing/_enrich_workout_exercise_media only sets thumbnail_url where the
// resolved object has that attribute, and a UserCustomExercise has none (its
// media lives in related ExerciseMedia rows), and it never adds a description.
// The public-share page also skips the bulk exercise list, so nothing hydrates
// the cards. The page therefore fetches each referenced custom exercise's full
// record (retrieve is open cross-profile — exercise_library/api/views.py:287,
// the same read iOS does) and hydrates the card thumbnails with these two.

/** Distinct `custom.*` exercise ids referenced by any group (recursing into
 *  supersets' nested groups) in a step list. */
export function collectCustomExerciseIds(steps: WorkoutStepElement[] | null | undefined): string[] {
  const ids = new Set<string>()
  const walk = (els: WorkoutStepElement[] | null | undefined) => {
    for (const el of els ?? []) {
      if (!isRepeatGroup(el)) continue
      const exId = el.exercise_id != null ? String(el.exercise_id) : ''
      if (exId.startsWith('custom.')) ids.add(exId)
      walk(el.steps)
    }
  }
  walk(steps)
  return Array.from(ids)
}

/** Hydrate a fetched custom exercise's display fields onto every group whose
 *  exercise_id matches, recursing into nested (superset) groups. The media
 *  thumbnail comes from the exercise's first media row (CustomExerciseSerializer
 *  `media`, ExerciseMediaSerializer `thumbnail_url`); empty media falls back to
 *  the group's existing thumbnail so the icon fallback still shows. */
export function hydrateCustomExerciseDetails(
  steps: WorkoutStepElement[],
  details: Map<string, Exercise>,
): WorkoutStepElement[] {
  if (!details.size) return steps
  const apply = (group: WorkoutRepeatGroup): WorkoutRepeatGroup => {
    const exId = group.exercise_id != null ? String(group.exercise_id) : ''
    const ex = details.get(exId)
    if (!ex) return group
    return {
      ...group,
      thumbnail_url: ex.media?.[0]?.thumbnail_url ?? ex.thumbnail_url ?? group.thumbnail_url,
      equipment: ex.equipment ?? group.equipment,
    }
  }
  const walk = (els: WorkoutStepElement[]): WorkoutStepElement[] => {
    let changed = false
    const out = els.map((el) => {
      if (!isRepeatGroup(el)) return el
      let group = apply(el as WorkoutRepeatGroup)
      if (group !== el) changed = true
      if (group.steps) {
        const walked = walk(group.steps)
        if (walked !== group.steps) {
          changed = true
          group = { ...group, steps: walked }
        }
      }
      return group
    })
    // Identity preserved when nothing changed, so callers can merge without
    // re-rendering cards that carry no custom exercise.
    return changed ? out : els
  }
  return walk(steps)
}

/** Find the first group whose exercise_id matches, recursing into nested
 *  groups. Returns undefined when no group references it. */
export function findGroupForExercise(
  steps: WorkoutStepElement[] | null | undefined,
  exerciseId: string | number | null,
): WorkoutRepeatGroup | undefined {
  if (exerciseId == null) return undefined
  const target = String(exerciseId)
  let found: WorkoutRepeatGroup | undefined
  const walk = (els: WorkoutStepElement[] | null | undefined) => {
    for (const el of els ?? []) {
      if (found || !isRepeatGroup(el)) continue
      if (String(el.exercise_id) === target) found = el
      else walk(el.steps)
    }
  }
  walk(steps)
  return found
}

/** Build a renderable Exercise from a shared workout's group for the detail
 *  dialog, for viewers whose detail read cannot succeed — a signed-out viewer
 *  holding only a share link gets a 401 from the authenticated detail endpoint
 *  (BEO-3201), so the dialog renders from the payload's own enrichment
 *  (thumbnail_url, exercise_description, media) instead. `media` is always an
 *  array (possibly empty) so ExerciseDetailView does not retry the 401ing
 *  fetch for a `media === undefined` record. */
export function exerciseFromSharedGroup(group: WorkoutRepeatGroup | undefined): Exercise | undefined {
  if (!group) return undefined
  const id = String(group.exercise_id)
  return {
    id,
    name: group.name ?? 'Exercise',
    type: id.startsWith('custom.') ? 'custom' : 'system',
    category: null,
    equipment: group.equipment ?? null,
    primary_muscles: [],
    description: group.exercise_description ?? undefined,
    thumbnail_url: group.thumbnail_url ?? null,
    media: group.media ?? [],
  }
}
